/**
 * Partner (ADR-0081, riveduto da ADR-0082) — i rivenditori, i loro cappelli, e la coda
 * di rivendicazione.
 *
 * L'ordine della pagina dice il modello: prima la CODA (l'unica cosa che chiede una
 * decisione, e che arriva da sola dagli heartbeat delle installazioni), poi l'elenco dei
 * partner con dentro persone e clienti.
 *
 * **La frase da tenere a mente, ed è scritta anche a schermo: approvare non apre niente.**
 * L'arco è attribuzione commerciale — mette il cliente nella lista del rivenditore. Per
 * ENTRARE nella sua istanza servono il consenso del cliente e il codice memorizzato che
 * combacia, verificati in locale dall'istanza (D5). Senza quel cartello, "approva" si legge
 * come "dai accesso", che è esattamente l'equivoco che il modello esiste per evitare.
 *
 * **Le persone sono ARCHI, non utenze (ADR-0082 D2).** Questa pagina non crea più account
 * con una password iniziale: dà il cappello a un Account Tray, legandolo se esiste e
 * invitandolo se no. Toglierlo non cancella nessuno — l'ex tecnico resta una persona, con
 * i suoi workspace e la sua storia, cosa che il vecchio "disabilita l'utente" non sapeva
 * dire.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Check,
  Handshake,
  Link2,
  Pencil,
  Plus,
  TriangleAlert,
  UserMinus,
  UserPlus,
  X,
} from "lucide-react";
import * as React from "react";

import { ClaimLink } from "@/components/platform/ClaimLink";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { cn } from "@/lib/cn";
import {
  type Partner,
  type PartnerClaim,
  type PartnerMember,
  type PartnerMemberAdded,
  type PartnerMemberRole,
  PlatformApiError,
  platformApi,
} from "@/lib/platform";

const PARTNERS_KEY = ["platform", "partners"];
const CLAIMS_KEY = ["platform", "partner-claims"];

function errorText(e: unknown, fallback = "Operazione non riuscita."): string {
  return e instanceof PlatformApiError ? e.message : fallback;
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

/** Da dove è arrivata la rivendicazione. `installer` è il caso normale: il codice è stato
 *  scritto nell'installazione e ha viaggiato sull'heartbeat. */
const SOURCE_LABEL: Record<string, string> = {
  installer: "Installazione",
  manual: "Manuale",
};

// --------------------------------------------------------------- dialoghi

function PartnerDialog({
  partner,
  onClose,
  onSaved,
}: {
  partner: Partner | null; // null = creazione
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = React.useState(partner?.code ?? "");
  const [name, setName] = React.useState(partner?.name ?? "");
  const [email, setEmail] = React.useState(partner?.contact_email ?? "");

  const save = useMutation({
    mutationFn: () =>
      partner
        ? platformApi.updatePartner(partner.id, {
            name: name.trim(),
            contact_email: email.trim() || null,
          })
        : platformApi.createPartner({
            code: code.trim().toLowerCase(),
            name: name.trim(),
            contact_email: email.trim() || null,
          }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const canSave = name.trim().length > 0 && (partner !== null || code.trim().length >= 3);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{partner ? `Modifica — ${partner.name}` : "Nuovo partner"}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="partner-code">Codice</Label>
            <Input
              id="partner-code"
              value={code}
              disabled={partner !== null}
              onChange={(e) => setCode(e.target.value)}
              placeholder="acme"
            />
            <p className="text-xs text-muted-foreground">
              {partner
                ? "Non modificabile: è la stringa che le istanze dei clienti confrontano col permesso firmato. Per togliere l'accesso usa Disabilita."
                : "Lo dai al rivenditore: lo scriverà nell'installazione dei suoi clienti. Minuscolo, senza spazi."}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="partner-name">Ragione sociale</Label>
            <Input
              id="partner-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ACME Rivenditori S.r.l."
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="partner-email">Email di contatto (opzionale)</Label>
            <Input
              id="partner-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="referente@acme.it"
            />
          </div>
          {save.isError && <p className="text-sm text-danger">{errorText(save.error)}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
            {partner ? "Salva" : "Crea partner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ROLE_LABEL: Record<PartnerMemberRole, string> = {
  admin: "Amministratore",
  technician: "Tecnico",
};

/**
 * Dare il cappello a una persona (ADR-0082 D2).
 *
 * Non c'è un campo password, ed è il punto: non teniamo mai una credenziale per conto di
 * qualcun altro. Se l'Account Tray esiste lo LEGHIAMO, altrimenti lo invitiamo e la
 * password se la sceglie lui raccogliendo l'invito.
 *
 * L'esito è una schermata, non un toast, perché porta il `claim_url`: **il link è la
 * garanzia, la mail è la comodità**. Con l'SMTP muto (o un indirizzo sbagliato) il
 * superadmin copia il link e lo passa al rivenditore per la sua strada — senza, l'invito
 * sarebbe partito e nessuno saprebbe dove.
 */
function AddMemberDialog({
  partner,
  onClose,
  onSaved,
}: {
  partner: Partner;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [email, setEmail] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [role, setRole] = React.useState<PartnerMemberRole>("technician");
  const [done, setDone] = React.useState<PartnerMemberAdded | null>(null);

  const save = useMutation({
    mutationFn: () =>
      platformApi.addPartnerMember(partner.id, {
        email: email.trim().toLowerCase(),
        full_name: fullName.trim() || null,
        role,
      }),
    onSuccess: (res) => {
      setDone(res);
      onSaved();
    },
  });

  if (done) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {done.mode === "linked" ? "Cappello assegnato" : "Invito inviato"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <p className="text-sm text-foreground">
              {done.mode === "linked" ? (
                <>
                  <strong>{done.member.email}</strong> aveva già un Account Tray: ora indossa
                  anche il cappello di {partner.name}. Accede con le credenziali che ha già.
                </>
              ) : (
                <>
                  Abbiamo creato l'Account Tray di <strong>{done.member.email}</strong>. La
                  password la sceglie lui raccogliendo l'invito — noi non ne teniamo una.
                </>
              )}
            </p>
            {!done.mail_sent && (
              <p className="text-sm text-warning">
                La mail non è partita. Passa tu il link qui sotto: senza, l'invito non arriva a
                nessuno.
              </p>
            )}
            {(done.claim_url || done.login_url) && (
              <ClaimLink url={(done.claim_url ?? done.login_url) as string} />
            )}
          </DialogBody>
          <DialogFooter>
            <Button onClick={onClose}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Aggiungi una persona — {partner.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Entra in Tray Central con il suo Account Tray. Se ne ha già uno lo colleghiamo,
            altrimenti riceve un invito: la password la sceglie lei.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="pm-email">Email</Label>
            <Input
              id="pm-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tecnico@acme.it"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pm-name">Nome e cognome (opzionale)</Label>
            <Input id="pm-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pm-role">Ruolo</Label>
            <select
              id="pm-role"
              value={role}
              onChange={(e) => setRole(e.target.value as PartnerMemberRole)}
            >
              <option value="technician">{ROLE_LABEL.technician}</option>
              <option value="admin">{ROLE_LABEL.admin}</option>
            </select>
          </div>
          {save.isError && <p className="text-sm text-danger">{errorText(save.error)}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || email.trim().length < 3}>
            Aggiungi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * L'esito di "Rigenera link". Due risposte, e la seconda è quella che conta.
 *
 * Se la persona non ha ancora una password, ecco il link nuovo da passarle. Se invece ce
 * l'ha già, il backend NON conia niente e qui si dice perché: un claim link è un reset
 * password, quindi questo bottone non deve poter prendere possesso di un account vivo —
 * e con esso dei workspace che quell'account raggiunge, che col rivenditore non c'entrano.
 */
function ReissuedLinkDialog({
  outcome,
  onClose,
}: {
  outcome: PartnerMemberAdded;
  onClose: () => void;
}) {
  const linked = outcome.mode === "linked";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {linked ? "Nessun link da rigenerare" : "Nuovo link d'accesso"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {linked ? (
            <p className="text-sm text-foreground">
              <strong>{outcome.member.email}</strong> ha già una password: accede con quella.
              Se l'ha persa, la reimposta lei dalla pagina di accesso — un link generato da
              qui sarebbe un reset password, e non spetta a questa console.
            </p>
          ) : (
            <>
              <p className="text-sm text-foreground">
                Passa questo link a <strong>{outcome.member.email}</strong>: sceglierà la
                password e da quel momento accede come partner. Scade tra 7 giorni.
              </p>
              {outcome.claim_url && <ClaimLink url={outcome.claim_url} />}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignOrganizationDialog({
  partner,
  onClose,
  onSaved,
}: {
  partner: Partner;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [search, setSearch] = React.useState("");
  const [picked, setPicked] = React.useState<string | null>(null);

  const orgsQ = useQuery({
    queryKey: ["platform", "organizations", "assign", search],
    queryFn: () => platformApi.listOrganizations({ search: search || undefined }),
  });

  const save = useMutation({
    mutationFn: () => platformApi.assignPartnerOrganization(partner.id, picked as string),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const orgs = orgsQ.data ?? [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assegna un cliente — {partner.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Registri una decisione già presa — un contratto, un cambio di rivenditore,
            un'installazione fatta prima che il codice esistesse. Il cliente comparirà nella lista
            del partner; <strong>non gli apri nessun accesso</strong>.
          </p>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca un'organizzazione…"
          />
          <div className="max-h-64 overflow-y-auto rounded-md border border-border/60">
            {orgs.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">Nessuna organizzazione trovata.</p>
            ) : (
              orgs.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setPicked(o.id)}
                  className={cn(
                    "flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-elevated",
                    picked === o.id && "bg-elevated font-medium text-foreground",
                  )}
                >
                  {o.name}
                  {picked === o.id && <Check className="h-4 w-4" strokeWidth={1.5} />}
                </button>
              ))
            )}
          </div>
          {save.isError && (
            <p className="text-sm text-danger">
              {errorText(save.error).includes("already_attributed")
                ? "Questo cliente è già di un altro partner: scollegalo prima da lì."
                : errorText(save.error)}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={() => save.mutate()} disabled={!picked || save.isPending}>
            Assegna
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------- coda

function ClaimsQueue() {
  const qc = useQueryClient();
  const claimsQ = useQuery({
    queryKey: CLAIMS_KEY,
    queryFn: () => platformApi.listPartnerClaims("pending"),
  });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: CLAIMS_KEY });
    void qc.invalidateQueries({ queryKey: PARTNERS_KEY });
  };
  const approve = useMutation({
    mutationFn: (c: PartnerClaim) => platformApi.approvePartnerClaim(c.id),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: (c: PartnerClaim) => platformApi.rejectPartnerClaim(c.id),
    onSuccess: invalidate,
  });

  const rows = claimsQ.data ?? [];
  if (rows.length === 0) return null;

  return (
    <div className="rounded-lg border border-warning/40 bg-warning/5 p-4">
      <h3 className="text-sm font-semibold text-foreground">
        Rivendicazioni da decidere ({rows.length})
      </h3>
      <p className="mb-3 mt-0.5 text-xs text-muted-foreground">
        Un'installazione ha dichiarato questo codice rivenditore. Approvando, il cliente compare
        nella lista del partner — <strong>non gli apri nessun accesso</strong>: per entrare servono
        il consenso del cliente e il codice che combacia, sulla sua istanza.
      </p>
      <div className="space-y-2">
        {rows.map((c) => (
          <div
            key={c.id}
            className="flex items-center justify-between gap-4 rounded-md border border-border/60 bg-surface px-3 py-2"
          >
            <div className="min-w-0 text-sm">
              <span className="font-medium text-foreground">
                {c.partner_name ?? "Partner sconosciuto"}
              </span>
              <span className="text-muted-foreground"> → </span>
              <span className="font-medium text-foreground">
                {c.organization_name ?? c.organization_id}
              </span>
              <div className="text-xs text-muted-foreground">
                {SOURCE_LABEL[c.source] ?? c.source} · {fmtDate(c.created_at)}
                {c.origin_ref ? ` · ${c.origin_ref}` : ""}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                title="Rifiuta"
                onClick={() => reject.mutate(c)}
                disabled={reject.isPending || approve.isPending}
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </Button>
              <Button
                size="sm"
                onClick={() => approve.mutate(c)}
                disabled={approve.isPending || reject.isPending}
              >
                <Check className="h-4 w-4" strokeWidth={1.5} />
                Approva
              </Button>
            </div>
          </div>
        ))}
      </div>
      {(approve.isError || reject.isError) && (
        <p className="mt-2 text-sm text-danger">
          {errorText(approve.error ?? reject.error, "Decisione non registrata.")}
        </p>
      )}
    </div>
  );
}

// --------------------------------------------------------------- dettaglio

function PartnerDetail({ partner }: { partner: Partner }) {
  const qc = useQueryClient();
  const [addingUser, setAddingUser] = React.useState(false);
  const [assigning, setAssigning] = React.useState(false);

  const membersQ = useQuery({
    queryKey: ["platform", "partner-members", partner.id],
    queryFn: () => platformApi.listPartnerMembers(partner.id),
  });
  const arcsQ = useQuery({
    queryKey: ["platform", "partner-arcs", partner.id],
    queryFn: () => platformApi.listPartnerOrganizations(partner.id),
  });
  const refreshMembers = () =>
    qc.invalidateQueries({ queryKey: ["platform", "partner-members", partner.id] });

  // The recovery for an invite that never landed. It lives next to the row, not in a
  // menu, because the person who needs it is staring at "invito da raccogliere".
  const [reissued, setReissued] = React.useState<PartnerMemberAdded | null>(null);
  const reissue = useMutation({
    mutationFn: (m: PartnerMember) => platformApi.reissuePartnerInvite(m.id),
    onSuccess: setReissued,
  });

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: PartnerMemberRole }) =>
      platformApi.setPartnerMemberRole(id, role),
    onSuccess: () => void refreshMembers(),
  });
  // Togliere il cappello NON cancella l'account: il testo del confirm lo dice, perché un
  // superadmin che teme di distruggere una persona semplicemente non clicca.
  const removeMember = useMutation({
    mutationFn: (m: PartnerMember) => platformApi.removePartnerMember(m.id),
    onSuccess: () => void refreshMembers(),
  });
  const revoke = useMutation({
    mutationFn: (organizationId: string) => platformApi.revokeOrganizationPartner(organizationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["platform", "partner-arcs", partner.id] });
      void qc.invalidateQueries({ queryKey: PARTNERS_KEY });
    },
  });

  const members = membersQ.data ?? [];
  const arcs = arcsQ.data ?? [];

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="rounded-lg border border-border/60 bg-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-foreground">Persone</h4>
          <Button size="sm" variant="ghost" onClick={() => setAddingUser(true)}>
            <UserPlus className="h-4 w-4" strokeWidth={1.5} />
            Aggiungi
          </Button>
        </div>
        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nessuna persona: il rivenditore non può ancora accedere a Central.
          </p>
        ) : (
          <div className="space-y-1.5">
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <span
                    className={m.state === "active" ? "text-foreground" : "text-muted-foreground"}
                  >
                    {m.full_name || m.email}
                  </span>
                  <div className="truncate text-xs text-muted-foreground">
                    {m.full_name ? `${m.email} · ` : ""}
                    {ROLE_LABEL[m.role] ?? m.role}
                    {/* Uno stato dell'ACCOUNT, non del cappello: un'identità disabilitata o un
                        invito mai raccolto spiegano da soli perché quella persona non entra. */}
                    {m.state !== "active" ? " · account disabilitato" : ""}
                    {m.state === "active" && !m.claimed ? " · invito da raccogliere" : ""}
                    {m.state === "active" && m.claimed && !m.email_verified
                      ? " · email da confermare"
                      : ""}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <select
                    aria-label={`Ruolo di ${m.email}`}
                    value={m.role}
                    disabled={setRole.isPending}
                    onChange={(e) =>
                      setRole.mutate({ id: m.id, role: e.target.value as PartnerMemberRole })
                    }
                  >
                    <option value="technician">{ROLE_LABEL.technician}</option>
                    <option value="admin">{ROLE_LABEL.admin}</option>
                  </select>
                  {!m.claimed && (
                    <Button
                      size="sm"
                      variant="ghost"
                      title="Rigenera il link d'accesso"
                      disabled={reissue.isPending}
                      onClick={() => reissue.mutate(m)}
                    >
                      <Link2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Togli il cappello (l'account resta)"
                    disabled={removeMember.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Togliere a ${m.email} il cappello di ${partner.name}?\n\n` +
                            "L'Account Tray non viene cancellato: restano la persona, i suoi " +
                            "workspace e la sua storia. Perde solo l'accesso a questo rivenditore.",
                        )
                      )
                        removeMember.mutate(m);
                    }}
                  >
                    <UserMinus className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {(setRole.isError || removeMember.isError || reissue.isError) && (
          <p className="mt-2 text-sm text-danger">
            {errorText(setRole.error ?? removeMember.error ?? reissue.error)}
          </p>
        )}
      </div>

      <div className="rounded-lg border border-border/60 bg-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-foreground">Clienti attribuiti</h4>
          <Button size="sm" variant="ghost" onClick={() => setAssigning(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Assegna
          </Button>
        </div>
        {arcs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nessun cliente. Arrivano da una rivendicazione approvata, o li assegni tu qui.
          </p>
        ) : (
          <div className="space-y-1.5">
            {arcs.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate text-foreground">
                  {a.organization_name ?? a.organization_id}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  title="Sciogli il legame"
                  onClick={() => revoke.mutate(a.organization_id)}
                  disabled={revoke.isPending}
                >
                  Scollega
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {addingUser && (
        <AddMemberDialog
          partner={partner}
          onClose={() => setAddingUser(false)}
          onSaved={() => void refreshMembers()}
        />
      )}
      {reissued && (
        <ReissuedLinkDialog outcome={reissued} onClose={() => setReissued(null)} />
      )}
      {assigning && (
        <AssignOrganizationDialog
          partner={partner}
          onClose={() => setAssigning(false)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ["platform", "partner-arcs", partner.id] });
            void qc.invalidateQueries({ queryKey: PARTNERS_KEY });
          }}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------- pagina

export function PlatformPartnersPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<Partner | null>(null);
  const [openId, setOpenId] = React.useState<string | null>(null);

  const listQ = useQuery({ queryKey: PARTNERS_KEY, queryFn: () => platformApi.listPartners() });
  const invalidate = () => void qc.invalidateQueries({ queryKey: PARTNERS_KEY });

  const toggle = useMutation({
    mutationFn: (p: Partner) => platformApi.updatePartner(p.id, { enabled: !p.enabled }),
    onSuccess: invalidate,
  });

  const rows = listQ.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Partner"
        description="I rivenditori, le persone che accedono a Tray Central e i clienti che tengono. L'attribuzione non è un accesso: entrare in un'istanza lo decide il cliente."
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Nuovo partner
          </Button>
        }
      />
      <PageContent className="space-y-4">
        <ClaimsQueue />

        {listQ.isError ? (
          <EmptyState
            icon={TriangleAlert}
            title="Impossibile caricare i partner"
            action={
              <Button variant="ghost" onClick={() => listQ.refetch()}>
                Riprova
              </Button>
            }
          />
        ) : rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={Handshake}
            title="Nessun partner"
            description="Crea il primo rivenditore: il codice che scegli qui è quello che scriverà nelle installazioni dei suoi clienti."
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Codice</Th>
                  <Th>Ragione sociale</Th>
                  <Th>Contatto</Th>
                  <Th>Clienti</Th>
                  <Th>Stato</Th>
                  <Th className="w-52 text-right">Azioni</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((p) => (
                  <React.Fragment key={p.id}>
                    <Tr>
                      <Td className="font-mono text-xs">{p.code}</Td>
                      <Td className="font-medium">{p.name}</Td>
                      <Td className="text-muted-foreground">{p.contact_email ?? "—"}</Td>
                      <Td>
                        <span className="flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                          {p.organization_count}
                        </span>
                      </Td>
                      <Td>
                        <Badge tone={p.enabled ? "active" : "neutral"}>
                          {p.enabled ? "Attivo" : "Disabilitato"}
                        </Badge>
                      </Td>
                      <Td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setOpenId(openId === p.id ? null : p.id)}
                          >
                            {openId === p.id ? "Chiudi" : "Dettaglio"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Modifica"
                            onClick={() => setEditing(p)}
                          >
                            <Pencil className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggle.mutate(p)}
                            disabled={toggle.isPending}
                          >
                            {p.enabled ? "Disabilita" : "Abilita"}
                          </Button>
                        </div>
                      </Td>
                    </Tr>
                    {openId === p.id && (
                      <Tr>
                        <Td colSpan={6} className="bg-elevated/40 p-4">
                          <PartnerDetail partner={p} />
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
          </div>
        )}

        {toggle.isError && <p className="text-sm text-danger">{errorText(toggle.error)}</p>}
      </PageContent>

      {creating && (
        <PartnerDialog partner={null} onClose={() => setCreating(false)} onSaved={invalidate} />
      )}
      {editing && (
        <PartnerDialog partner={editing} onClose={() => setEditing(null)} onSaved={invalidate} />
      )}
    </PageContainer>
  );
}
